import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, LOGO_URL } from "@/data";

const NAV = [
  { to: "/", label: "Start" },
  { to: "/leistungen", label: "Leistungen" },
  { to: "/ueber-uns", label: "Über uns" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || open ? "glass-dark py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 md:px-8 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <img src={LOGO_URL} alt="M&A Objektservice" className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/40" />
            <span className="font-serif text-lg md:text-xl tracking-tight text-white leading-none">
              M&amp;A <span className="text-gold">Objektservice</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`text-sm tracking-wide transition-colors hover:text-gold ${
                  pathname === n.to ? "text-gold" : "text-white/80"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/kontakt"
              data-testid="header-cta"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_25px_rgba(201,169,97,0.4)]"
            >
              Kostenlose Besichtigung
            </Link>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="header-call"
              className="md:hidden grid h-10 w-10 place-items-center rounded-full bg-gold text-ink"
              aria-label="Anrufen"
            >
              <Phone size={18} />
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="menu-toggle"
              className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white"
              aria-label="Menü"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-ink lg:hidden grain"
          >
            <div className="flex h-full flex-col justify-center px-8 gap-2">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                >
                  <Link to={n.to} className="font-serif text-5xl text-white hover:text-gold transition-colors">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <Link
                to="/kontakt"
                className="mt-8 inline-flex w-fit rounded-full bg-gold px-6 py-3 text-ink font-medium"
              >
                Kostenlose Besichtigung
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
